import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
    selector: '[nonNegative]'
})
export class NonNegativeDirective {
    private regex: RegExp = /^[0-9]*\.?[0-9]*$/;
    private allowedKeys: string[] = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
    ];
    private allowedCtrlKeys: string[] = ['a', 'c', 'v', 'x'];

    constructor(private el: ElementRef<HTMLInputElement>) {}

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        const inputValue = this.el.nativeElement.value;

        if (
            this.allowedKeys.includes(event.key) ||
            (event.ctrlKey && this.allowedCtrlKeys.includes(event.key.toLowerCase()))
        ) {
            return;
        }

        if (event.key === '.' && !inputValue.includes('.')) {
            return;
        }

        if (!/^[0-9]$/.test(event.key)) {
            event.preventDefault();
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pastedInput = event.clipboardData?.getData('text/plain') || '';
        const sanitized = this.sanitizeValue(pastedInput);
        this.insertText(sanitized);
    }

    @HostListener('input', ['$event'])
    onInput(event: Event) {
        const input = this.el.nativeElement;
        const currentValue = input.value;
        const cursorPosition = input.selectionStart || 0;

        const sanitizedValue = this.sanitizeValue(currentValue);

        if (currentValue !== sanitizedValue) {
            input.value = sanitizedValue;
            const newCursorPosition = Math.max(0, cursorPosition - (currentValue.length - sanitizedValue.length));
            input.setSelectionRange(newCursorPosition, newCursorPosition);

            const inputEvent = new Event('input', { bubbles: true });
            input.dispatchEvent(inputEvent);
        }
    }

    private sanitizeValue(value: string): string {
        const parts = value.split('.');
        const integerPart = parts[0].replace(/[^0-9]/g, '');
        const decimalPart = parts[1]?.replace(/[^0-9]/g, '');
        return parts.length > 1 ? `${integerPart}.${decimalPart ?? ''}` : integerPart;
    }

    private insertText(text: string) {
        const input = this.el.nativeElement;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const value = input.value;

        const newValue = value.slice(0, start) + text + value.slice(end);
        const sanitized = this.sanitizeValue(newValue);

        input.value = sanitized;

        const newCursorPosition = start + sanitized.length - (value.length - (end - start));
        input.setSelectionRange(newCursorPosition, newCursorPosition);

        const inputEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(inputEvent);
    }
}
